import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { createTenantStorage } from './tenantStorage';
import type { StateStorage } from 'zustand/middleware';

type FeedbackDraft = {
  traceId: string;
  feedback: string;
  screenshot?: {
    data: string;
    mimeType: string;
    fileName: string;
    size: number;
  };
};

interface FeedbackDraftsState {
  currentDraft: FeedbackDraft | null;
  getDraft: (traceId: string) => FeedbackDraft | null;
  updateFeedbackText: (traceId: string, feedback: string) => void;
  updateScreenshot: (
    traceId: string,
    screenshot: FeedbackDraft['screenshot'],
  ) => void;
  clearDraft: () => void;
}

const STORAGE_KEY = 'copilot-feedback-draft';

const createSafeTenantStorage = (): StateStorage => {
  const tenantStorage = createTenantStorage();
  return {
    getItem: (name: string) => {
      try {
        return tenantStorage.getItem(name);
      } catch (error) {
        console.error('Failed to read from localStorage:', error);
        return null;
      }
    },
    setItem: (name: string, value: string) => {
      try {
        tenantStorage.setItem(name, value);
      } catch (error) {
        console.error(
          'Failed to write to localStorage (quota exceeded?):',
          error,
        );
        try {
          tenantStorage.removeItem(name);
        } catch {
          // Ignore cleanup errors
        }
      }
    },
    removeItem: (name: string) => {
      try {
        tenantStorage.removeItem(name);
      } catch (error) {
        console.error('Failed to remove from localStorage:', error);
      }
    },
  };
};

export const useFeedbackDraftsStore = create<FeedbackDraftsState>()(
  persist(
    (set, get) => ({
      currentDraft: null,

      getDraft: (traceId: string) => {
        const { currentDraft } = get();
        if (currentDraft?.traceId === traceId) {
          return currentDraft;
        }
        return null;
      },

      updateFeedbackText: (traceId: string, feedback: string) => {
        set((state) => {
          const isCurrentTrace = state.currentDraft?.traceId === traceId;
          const existingScreenshot = isCurrentTrace
            ? state.currentDraft?.screenshot
            : undefined;

          if (!feedback.trim() && !existingScreenshot) {
            return { currentDraft: null };
          }

          return {
            currentDraft: {
              traceId,
              feedback,
              screenshot: existingScreenshot,
            },
          };
        });
      },

      updateScreenshot: (
        traceId: string,
        screenshot: FeedbackDraft['screenshot'],
      ) => {
        set((state) => {
          const isCurrentTrace = state.currentDraft?.traceId === traceId;
          const existingFeedback = isCurrentTrace
            ? state.currentDraft?.feedback || ''
            : '';

          if (!screenshot && !existingFeedback.trim()) {
            return { currentDraft: null };
          }

          return {
            currentDraft: {
              traceId,
              feedback: existingFeedback,
              screenshot,
            },
          };
        });
      },

      clearDraft: () => {
        set({ currentDraft: null });
      },
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => createSafeTenantStorage()),
    },
  ),
);
